"use client";

import CtaButton from "@/components/ui/buttons/CtaButton";
import HighlightLastWord from "@/utils/highlightLastWord";
import * as Sentry from "@sentry/nextjs";
import { useRouter } from "next/navigation";
import { startTransition, useEffect, useState } from "react";

/**
 * Error boundary for every page under (pages), so a failed render degrades into
 * this panel — inside the layout, nav and footer intact — instead of unwinding
 * to app/global-error.tsx, which replaces the whole document with a bare
 * "Application error" screen.
 *
 * The case that prompted it: a client navigation whose RSC payload arrives with
 * a 200 and is then cut off mid-stream. Chrome reports the truncated body as
 * "TypeError: network error", React cannot finish the tree, and without a
 * boundary here the visitor lost the entire page over a transient network blip.
 *
 * Most of these are not worth showing the panel for. The truncated payload is a
 * one-off, and the browser's own request for the same URL nearly always
 * succeeds, so a network error reloads the page once before the panel is ever
 * painted. Nothing is lost by it: by the time this boundary renders, the page
 * under it — order form included — has already been unmounted.
 *
 * Root layout failures still land in global-error.tsx — a boundary inside the
 * layout cannot catch the layout that renders it.
 */

// How each engine words a fetch that failed at the transport layer — Chrome's
// "network error" is the one a cut-off response body raises. Kept in step with
// NETWORK_TYPE_ERRORS in sentry.noise.js.
const NETWORK_ERROR =
  /^(?:network error|Failed to fetch|Load failed|NetworkError when attempting to fetch resource\.)/;

const RELOADED_KEY = "page-error-reloaded";

/**
 * Whether this URL may still be reloaded. Once per URL, so a page that fails on
 * every load shows the panel instead of looping. Without sessionStorage the
 * second failure cannot be told from the first, so it never reloads at all.
 */
function canReload(): boolean {
  try {
    return sessionStorage.getItem(RELOADED_KEY) !== window.location.href;
  } catch {
    return false;
  }
}

/** Reloads only once the marker has stuck; a failed write would loop. */
function reload(): boolean {
  try {
    sessionStorage.setItem(RELOADED_KEY, window.location.href);
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

export default function PageError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  const router = useRouter();

  // Decided before the first paint, so the panel never flashes ahead of the
  // reload.
  const [recovering, setRecovering] = useState(
    () => NETWORK_ERROR.test(error.message) && canReload(),
  );

  useEffect(() => {
    if (recovering) {
      // Still reported, so the rate stays visible, but as a warning: the
      // visitor never sees the failure.
      Sentry.captureException(error, {
        level: "warning",
        tags: { source: "page-error-boundary", recovery: "reload" },
        extra: { digest: error.digest },
      });
      if (reload()) return;
      // Storage refused the write (quota): show the panel instead. A single
      // extra render on a path that is already an error is not the cascade
      // the rule guards against.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setRecovering(false);
      return;
    }

    // global-error.tsx used to be the only reporter; now that this boundary
    // catches first, the event would disappear from Sentry unless it reports
    // too. The source tag also keeps it past the noise filter, which drops
    // anything whose frames are all third-party — Next's router internals sit
    // on this stack and none of them are files we could open.
    Sentry.captureException(error, {
      level: "error",
      tags: { source: "page-error-boundary" },
      extra: { digest: error.digest },
    });
  }, [error, recovering]);

  const retry = () => {
    // reset() alone replays the same failed server render, so refresh the
    // route's data first and reset inside the same transition.
    startTransition(() => {
      router.refresh();
      reset();
    });
  };

  if (recovering) return null;

  return (
    <div className='mx-6 lg:mx-25 py-50 xl:mx-75 mt-20 flex flex-col items-center text-center'>
      <h1 className='text-5xl md:text-6xl leading-16 font-bold font-kalam mb-6'>
        <HighlightLastWord title='Ups! Niečo sa pokazilo' />
      </h1>

      <p className='text-[15px] mb-8 max-w-xl'>
        Stránku sa nepodarilo načítať — pravdepodobne len zlyhalo spojenie 📡
        Skús to prosím znova, alebo sa vráť domov 🌍
      </p>

      <div className='flex gap-4'>
        <button
          type='button'
          onClick={retry}
          className='bg-[#619F1C] text-white cursor-pointer font-medium px-6 py-3 rounded-full hover:bg-[#D27140] transition-colors duration-200'
        >
          <p
            className='min-w-32.5'
            style={{ textShadow: "0 0 1px rgba(0,0,0,0.35)" }}
          >
            Skúsiť znova
          </p>
        </button>
        <CtaButton text='Domov' href='/' />
      </div>
    </div>
  );
}
